/**
 * /smartlinks/fraud — SmartLink anti-fraud pre-filter operator page.
 *
 * Declares a TanStack Router route and renders the headline + the
 * `SmartlinkFraudPage` feature. The route is auto-picked up by the root
 * wire-up cycle which imports from this module into `router.tsx`.
 *
 * A `?smartlinkId=` search param deep-links the page to a specific
 * SmartLink scope so operators can jump here from a SmartLink detail view
 * with the scope pre-filled.
 */

import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { SmartlinkFraudPage } from "@/features/smartlinks-fraud/SmartlinkFraudPage";

// ---------------------------------------------------------------------------
// Route — mounts at `/smartlinks/fraud`.
// ---------------------------------------------------------------------------

interface SmartlinksFraudSearch {
	smartlinkId?: string;
}

export const smartlinksFraudRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/smartlinks/fraud",
	validateSearch: (raw: Record<string, unknown>): SmartlinksFraudSearch => {
		const v = raw.smartlinkId;
		if (typeof v === "string" && v.trim().length > 0) {
			return { smartlinkId: v.trim() };
		}
		return {};
	},
	component: SmartlinksFraudRoute,
});

export default smartlinksFraudRoute;

// ---------------------------------------------------------------------------
// Route shell — breadcrumb + page. Keep the breadcrumb purely presentational
// and brand-compliant (no emojis, no competitor naming).
// ---------------------------------------------------------------------------

export function SmartlinksFraudRoute() {
	const { smartlinkId } = smartlinksFraudRoute.useSearch();

	return (
		<div className="flex flex-col">
			<nav
				aria-label="Breadcrumb"
				className="mx-auto w-full max-w-5xl px-6 pt-10 text-sm text-zinc-500 sm:pt-16"
			>
				<ol className="flex flex-wrap items-center gap-1">
					<li>
						<Link
							to="/"
							className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
						>
							Dashboard
						</Link>
					</li>
					<li aria-hidden="true" className="text-zinc-300">
						/
					</li>
					<li>
						<span className="rounded-[6px] px-1 py-0.5">SmartLinks</span>
					</li>
					<li aria-hidden="true" className="text-zinc-300">
						/
					</li>
					<li className="text-zinc-900">Anti-fraud</li>
				</ol>
			</nav>

			<SmartlinkFraudPage initialSmartlinkId={smartlinkId} />
		</div>
	);
}

// Named export for the root wire-up cycle.
export { SmartlinksFraudRoute as SmartlinksFraudPage };
